import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { setTokens, removeTokens } from 'src/utils/jwt';
import { authRefresh } from 'src/services/authService';
import { ResponseCode } from 'src/types/services';
import { unescape } from 'lodash';

const paramsForApiCreation = {
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'x-humand-origin': 'web',
  },
};

const api = axios.create(paramsForApiCreation);

export const apiForRefresh = axios.create(paramsForApiCreation);

type RetryQueueItem = {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
};

const refreshAndRetryQueue: RetryQueueItem[] = [];

let isRefreshing = false;

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest: AxiosRequestConfig = error.config;
    let newAccessToken, newRefreshToken;

    if (error?.response?.data?.code === ResponseCode.TOKEN_EXPIRED) {
      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          refreshAndRetryQueue.push({
            config: originalRequest,
            resolve,
            reject,
          });
        });
      }
      isRefreshing = true;
      try {
        const refreshResponse = await authRefresh();
        const { accessToken, refreshToken } = refreshResponse.data;
        newAccessToken = accessToken;
        newRefreshToken = refreshToken;

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        const newResponse = await api(originalRequest);

        setTokens(newAccessToken, newRefreshToken);

        isRefreshing = false;

        refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
          config.headers.Authorization = `Bearer ${newAccessToken}`;
          api(config).then(resolve).catch(reject);
        });

        return newResponse;
      } catch (e) {
        removeTokens();
        isRefreshing = false;
        window.location.replace('/');
      }
    }
    return Promise.reject(error);
  },
);

const FILTERED_MODULES = [
  'articles',
  'tickets',
  'forms',
  'documents',
  'knowledge-libraries',
  'chats',
  'users',
  'segmentation-groups',
  'auth',
  'instances',
];

const unescapeStrings = (data: unknown) => {
  if (typeof data === 'string') {
    return unescape(data);
  } else if (Array.isArray(data)) {
    return data.map(unescapeStrings);
  } else if (data !== null && typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = unescapeStrings(data[key]);
      return acc;
    }, {});
  }
  return data;
};

const unescapeInterceptor = (response: AxiosResponse) => {
  const { url } = response.config;
  const mainPath = url.split('/')[url.startsWith('/') ? 1 : 0];

  if (FILTERED_MODULES.includes(mainPath)) {
    response.data = unescapeStrings(response.data);
  }

  return response;
};

api.interceptors.response.use(unescapeInterceptor);

export const scormApi = axios.create({
  baseURL: 'https://cloud.scorm.com/api/v2',
  headers: {
    Authorization:
      'Basic NjU3TjRJUllDVDpnVmFSWTJEUExDZ215SUIzeVA2a2tDUmpZcjE4aFJzOUVxMkx2cVdS',
  },
});

export default api;
